/**
 * Phase 8 · C-49 · Shared viz — ApplicationFunnel.
 *
 * Four horizontal bars stacked; width proportional to the widest stage;
 * percentage on the right. Clicking a stage fires `onClickStage(stage)`.
 */
import { ChevronDown } from 'lucide-react';
import './tokens.css';

export type FunnelStage = 'applied' | 'shortlisted' | 'interviewing' | 'hired';

export interface ApplicationFunnelCounts {
  applied: number;
  shortlisted: number;
  interviewing: number;
  hired: number;
}

export interface ApplicationFunnelProps {
  counts: ApplicationFunnelCounts;
  onClickStage?: (stage: FunnelStage) => void;
}

const STAGES: { key: FunnelStage; label: string; color: string }[] = [
  { key: 'applied',      label: 'Applied',      color: 'var(--viz-neutral)' },
  { key: 'shortlisted',  label: 'Shortlisted',  color: 'var(--viz-success)' },
  { key: 'interviewing', label: 'Interviewing', color: 'var(--viz-warning)' },
  { key: 'hired',        label: 'Hired',        color: 'var(--viz-success)' },
];

export function ApplicationFunnel({ counts, onClickStage }: ApplicationFunnelProps) {
  const max = Math.max(1, counts.applied, counts.shortlisted, counts.interviewing, counts.hired);
  const total = counts.applied || 1;

  return (
    <div className="flex flex-col gap-1.5" role="list" aria-label="Application funnel">
      {STAGES.map((stage, i) => {
        const value = counts[stage.key];
        const widthPct = Math.max(4, Math.round((value / max) * 100));
        const sharePct = Math.round((value / total) * 100);
        const interactive = Boolean(onClickStage);

        const Inner = (
          <>
            <div className="flex items-center justify-between text-[11px]" style={{ color: 'var(--viz-text-muted)' }}>
              <span className="font-medium">{stage.label}</span>
              <span className="tabular-nums">
                <span style={{ color: 'var(--viz-text)' }}>{value}</span>
                {' '}· {sharePct}%
              </span>
            </div>
            <div
              className="mt-0.5 h-2 rounded-full"
              style={{ background: 'var(--viz-track)' }}
              aria-hidden
            >
              <div
                className="h-full rounded-full transition-[width] duration-300"
                style={{ width: `${widthPct}%`, background: stage.color }}
              />
            </div>
          </>
        );

        return (
          <div key={stage.key} role="listitem">
            {interactive ? (
              <button
                type="button"
                onClick={() => onClickStage?.(stage.key)}
                className="block w-full rounded-md px-1 py-0.5 text-left outline-none focus:ring-2 focus:ring-offset-0"
                style={{ color: 'var(--viz-text)' }}
              >
                {Inner}
              </button>
            ) : (
              <div className="px-1 py-0.5">{Inner}</div>
            )}
            {i < STAGES.length - 1 && (
              <div className="my-0.5 flex justify-center" aria-hidden>
                <ChevronDown className="h-3 w-3" style={{ color: 'var(--viz-text-muted)' }} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
